import * as React from 'react'

import { Box, Text, useClassNames } from '@factorialco/deprecated-design-system'
import { useResizeObserver } from 'usehooks-ts'

import './index.styles.css'

const stylesRefs = {
  slider: 'SingleRangeSlider_slider',
  min: 'SingleRangeSlider_min',
  minDrag: 'SingleRangeSlider_minDrag',
  disabled: 'SingleRangeSlider_disabled',
  readonly: 'SingleRangeSlider_readonly',
}

interface Props {
  min: number
  max: number
  value: number
  onChange: (value: number) => void
  readonly?: boolean
}

export const SingleRangeSlider = (props: Props) => {
  const { min, max, value, onChange, readonly } = props
  const sliderRef = React.useRef<HTMLDivElement | null>(null)
  const minHandleRef = React.useRef<HTMLDivElement | null>(null)
  const { width: sliderWidth = 0 } = useResizeObserver({ ref: sliderRef })

  // Converts from slider's pixel space to min–max range value.
  // The coordinate parameter must be with respect to the slider's left edge.
  const coordinateToValue = (coordinate: number) => {
    return Math.round(((max - min) * coordinate) / sliderWidth + min)
  }

  React.useEffect(() => {
    const valueToCoordinate = (v: number) => {
      return ((v - min) / (max - min)) * sliderWidth
    }

    const coordinate = valueToCoordinate(Math.max(value, min))

    if (minHandleRef.current) {
      minHandleRef.current.style.width = `${coordinate}px`
    }
  }, [sliderWidth, min, max, value])

  const handleMouseMove = (e: MouseEvent) => {
    if (!sliderRef.current) return

    const sliderRect = sliderRef.current.getBoundingClientRect()
    const draggedWidth = e.clientX - sliderRect.left

    if (draggedWidth >= 0 && draggedWidth <= sliderWidth) {
      onChange(coordinateToValue(draggedWidth))
    }
  }

  const handleMouseUp = () => {
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('mousemove', handleMouseMove)
  }

  const handleMouseDown = (event: React.SyntheticEvent) => {
    event.preventDefault()
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const cx = useClassNames(stylesRefs)

  return (
    <Box
      role="slider"
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      paddingBottom="s40"
    >
      <Text size="200">{min}</Text>
      <div ref={sliderRef} className={cx('slider', { disabled: readonly })}>
        <div ref={minHandleRef} className={cx('min')} data-content={value}>
          <div
            className={cx('minDrag', {
              readonly,
            })}
            onMouseDown={handleMouseDown}
          />
        </div>
      </div>
      <Text size="200">{max}</Text>
    </Box>
  )
}
